import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { api, aiApi, githubApi, AIProviderConfig } from '../services/api'
import { User, Lock, Mail, User as UserIcon, AlertCircle, CheckCircle, Key, Trash2, Github } from 'lucide-react'
import toast from 'react-hot-toast'

export default function SettingsPage() {
  const { user, updateUser } = useAuthStore()
  
  // Profile state
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [isProfileLoading, setIsProfileLoading] = useState(false)
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  
  // API Keys state
  const [anthropicApiKey, setAnthropicApiKey] = useState('')
  const [googleApiKey, setGoogleApiKey] = useState('')
  const [isApiKeysLoading, setIsApiKeysLoading] = useState(false)
  const [apiKeysSaved, setApiKeysSaved] = useState(false)
  
  // AI Provider state
  const [providers, setProviders] = useState<AIProviderConfig[]>([])
  const [apiKeys, setApiKeys] = useState<Array<{ provider: string; isActive: boolean; lastUsedAt: string }>>([])
  const [selectedProvider, setSelectedProvider] = useState('')
  const [providerApiKey, setProviderApiKey] = useState('')
  const [isProviderLoading, setIsProviderLoading] = useState(false)
  const [providerMessage, setProviderMessage] = useState('')
  
  // GitHub state
  const [githubStatus, setGithubStatus] = useState<{ connected: boolean; username?: string } | null>(null)
  const [githubLoading, setGithubLoading] = useState(false)
  const [githubMessage, setGithubMessage] = useState('')
  
  useEffect(() => {
    if (user) {
      setName(user.name || '')
      setEmail(user.email)
    }
  }, [user])
  
  // Load existing API keys
  useEffect(() => {
    const loadApiKeys = async () => {
      try {
        const response = await api.get('/auth/me')
        const userData = response.data.user
        if (userData.anthropicApiKey) setAnthropicApiKey(userData.anthropicApiKey)
        if (userData.googleApiKey) setGoogleApiKey(userData.googleApiKey)
      } catch (error) {
        console.error('Failed to load API keys:', error)
      }
    }
    loadApiKeys()
  }, [])
  
  // Load AI providers and keys
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const response = await aiApi.getProviders()
        setProviders(response.data.data)
      } catch (error) {
        console.error('Failed to load providers:', error)
      }
    }
    loadProviders()
  }, [])
  
  useEffect(() => {
    const loadProviderKeys = async () => {
      try {
        const response = await aiApi.getKeys()
        setApiKeys(response.data.data)
      } catch (error) {
        console.error('Failed to load API keys:', error)
      }
    }
    loadProviderKeys()
  }, [])
  
  // Load GitHub status and check for OAuth callback
  useEffect(() => {
    const loadGithubStatus = async () => {
      try {
        const response = await githubApi.getStatus()
        setGithubStatus(response.data)
      } catch (error) {
        console.error('Failed to load GitHub status:', error)
      }
    }
    loadGithubStatus()
    
    // Check URL params for OAuth result
    const params = new URLSearchParams(window.location.search)
    if (params.get('github_connected')) {
      setGithubMessage('GitHub connected successfully!')
      // Clean URL
      window.history.replaceState({}, '', '/settings')
    }
    if (params.get('github_error')) {
      setGithubMessage(`GitHub error: ${params.get('github_error')}`)
      window.history.replaceState({}, '', '/settings')
    }
  }, [])
  
  const handleConnectGitHub = async () => {
    setGithubLoading(true)
    setGithubMessage('')
    try {
      const response = await githubApi.authorize()
      window.location.href = response.data.authUrl
    } catch (error: any) {
      setGithubLoading(false)
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to connect GitHub'
      setGithubMessage(msg)
      toast.error(msg)
    }
  }
  
  const handleDisconnectGitHub = async () => {
    if (!confirm('Are you sure you want to disconnect GitHub?')) return
    
    setGithubLoading(true)
    setGithubMessage('')
    try {
      await githubApi.disconnect()
      setGithubStatus({ connected: false })
      setGithubMessage('GitHub disconnected')
      toast.success('GitHub disconnected')
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to disconnect GitHub'
      setGithubMessage(msg)
      toast.error(msg)
    } finally {
      setGithubLoading(false)
    }
  }
  
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsProfileLoading(true)
    
    try {
      const response = await api.patch('/auth/profile', { name, email })
      updateUser(response.data.user)
      toast.success('Profile updated successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setIsProfileLoading(false)
    }
  }
  
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }
    
    setIsPasswordLoading(true)
    
    try {
      await api.put('/auth/password', { currentPassword, newPassword })
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change password')
    } finally {
      setIsPasswordLoading(false)
    }
  }
  
  const handleSaveApiKeys = async () => {
    setIsApiKeysLoading(true)
    setApiKeysSaved(false)
    
    try {
      await api.put('/auth/api-keys', { anthropicApiKey, googleApiKey })
      setApiKeysSaved(true)
      toast.success('API keys saved successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save API keys')
    } finally {
      setIsApiKeysLoading(false)
    }
  }
  
  const handleSaveProviderKey = async () => {
    if (!selectedProvider || !providerApiKey.trim()) {
      setProviderMessage('Please select a provider and enter an API key')
      return
    }
    
    setIsProviderLoading(true)
    setProviderMessage('')
    
    try {
      await aiApi.setKey(selectedProvider, providerApiKey)
      setProviderMessage('API key saved successfully')
      setProviderApiKey('')
      setSelectedProvider('')
      // Reload keys
      const response = await aiApi.getKeys()
      setApiKeys(response.data.data)
      const providersResponse = await aiApi.getProviders()
      setProviders(providersResponse.data.data)
      toast.success('API key saved successfully')
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to save API key'
      setProviderMessage(msg)
      toast.error(msg)
    } finally {
      setIsProviderLoading(false)
    }
  }
  
  const handleDeleteProviderKey = async (provider: string) => {
    if (!confirm(`Delete API key for ${provider}?`)) return
    
    try {
      await aiApi.deleteKey(provider)
      setProviderMessage('API key deleted')
      // Reload keys
      const response = await aiApi.getKeys()
      setApiKeys(response.data.data)
      const providersResponse = await aiApi.getProviders()
      setProviders(providersResponse.data.data)
      toast.success('API key deleted')
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to delete API key'
      setProviderMessage(msg)
      toast.error(msg)
    }
  }
  
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>
      
      {/* Profile Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <UserIcon className="w-5 h-5" />
          Profile
        </h2>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input"
            />
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={isProfileLoading}
          >
            {isProfileLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
      
      {/* Password Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Change Password
        </h2>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="input"
              required
            />
          </div>
          {passwordError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{passwordError}</span>
            </div>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={isPasswordLoading}
          >
            {isPasswordLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
      
      {/* Legacy API Keys Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Legacy API Keys</h2>
        <p className="text-dark-400 mb-4 text-sm">Configure AI provider API keys for chat functionality (legacy method).</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Claude API Key</label>
            <input
              type="password"
              value={anthropicApiKey}
              onChange={(e) => {
                setAnthropicApiKey(e.target.value)
                setApiKeysSaved(false)
              }}
              placeholder="sk-ant-..."
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">Gemini API Key</label>
            <input
              type="password"
              value={googleApiKey}
              onChange={(e) => {
                setGoogleApiKey(e.target.value)
                setApiKeysSaved(false)
              }}
              placeholder="AIza..."
              className="input"
            />
          </div>
          
          {apiKeysSaved && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">API keys saved successfully</span>
            </div>
          )}
          
          <button
            type="button"
            onClick={handleSaveApiKeys}
            className="btn-primary"
            disabled={isApiKeysLoading}
          >
            {isApiKeysLoading ? 'Saving...' : 'Save API Keys'}
          </button>
        </div>
      </div>
      
      {/* AI Provider Keys Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          AI Provider API Keys
        </h2>
        <p className="text-dark-400 mb-4 text-sm">Manage AI provider API keys for advanced features.</p>
        
        {/* Existing Keys */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-dark-200 mb-2">Configured Keys</h3>
          {apiKeys.length === 0 ? (
            <p className="text-dark-400 text-sm">No API keys configured</p>
          ) : (
            <ul className="space-y-2">
              {apiKeys.map(key => (
                <li key={key.provider} className="flex items-center justify-between bg-dark-800 p-3 rounded">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">{providers.find(p => p.name === key.provider)?.displayName || key.provider}</span>
                    <span className={`text-xs px-2 py-1 rounded ${key.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {key.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteProviderKey(key.provider)}
                    className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {/* Add New Key */}
        <div className="bg-dark-800 p-4 rounded">
          <h3 className="text-sm font-medium text-dark-200 mb-3">Add API Key</h3>
          
          <div className="flex gap-3 mb-3">
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded text-white"
            >
              <option value="">Select provider...</option>
              {providers.filter(p => p.requiresApiKey).map(p => (
                <option key={p.name} value={p.name}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-3 mb-3">
            <input
              type="password"
              value={providerApiKey}
              onChange={(e) => setProviderApiKey(e.target.value)}
              placeholder="Enter API key"
              className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded text-white"
            />
            <button
              onClick={handleSaveProviderKey}
              disabled={isProviderLoading || !selectedProvider || !providerApiKey}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProviderLoading ? 'Saving...' : 'Save Key'}
            </button>
          </div>
          
          {providerMessage && (
            <p className={`text-sm ${providerMessage.includes('Failed') || providerMessage.includes('Please') ? 'text-red-400' : 'text-green-400'}`}>
              {providerMessage}
            </p>
          )}
        </div>
      </div>
      
      {/* Account Info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Account
        </h2>
        <div className="bg-dark-800 p-4 rounded space-y-2">
          <p className="text-dark-200"><span className="font-medium text-white">Email:</span> {user?.email}</p>
          <p className="text-dark-200"><span className="font-medium text-white">Plan:</span> {user?.subscriptionTier?.toUpperCase() || 'FREE'}</p>
          <p className="text-dark-200"><span className="font-medium text-white">Role:</span> {user?.role}</p>
        </div>
      </div>
      
      {/* GitHub Connection */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Github className="w-5 h-5" />
          GitHub Integration
        </h2>
        <p className="text-dark-400 mb-4 text-sm">Connect your GitHub account to clone repositories and sync changes.</p>
        
        {githubStatus?.connected ? (
          <div className="bg-dark-800 p-4 rounded">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                  <Github className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-white">Connected to GitHub</p>
                  <p className="text-sm text-dark-400">@{githubStatus.username}</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                Connected
              </span>
            </div>
            <button
              onClick={handleDisconnectGitHub}
              disabled={githubLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
            >
              {githubLoading ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <div className="bg-dark-800 p-4 rounded">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                <Github className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="font-medium text-white">Connect GitHub</p>
                <p className="text-sm text-dark-400">Not connected</p>
              </div>
            </div>
            <button
              onClick={handleConnectGitHub}
              disabled={githubLoading}
              className="px-4 py-2 bg-gray-800 border border-gray-600 hover:bg-gray-700 text-white rounded flex items-center gap-2 disabled:opacity-50"
            >
              <Github className="w-4 h-4" />
              {githubLoading ? 'Connecting...' : 'Connect GitHub'}
            </button>
          </div>
        )}
        
        {githubMessage && (
          <div className={`mt-4 p-3 rounded-lg ${githubMessage.includes('error') || githubMessage.includes('Failed') ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'}`}>
            <p className={`text-sm ${githubMessage.includes('error') || githubMessage.includes('Failed') ? 'text-red-400' : 'text-green-400'}`}>
              {githubMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
